import { formatDateKey } from '@/js/core/utils/formatters';
import { monthToCalendarGridRange, monthToRange } from '@/js/core/utils/month-range.js';

const TIME_SPANS = ['this_month', 'upcoming', 'today'];
const LAYOUTS = ['list', 'calendar', 'grid'];

export const EVENT_LIST_CALENDAR_GROUP_IDS = ['academic_calendar', 'reviews', 'events'];

export const EVENT_LIST_CALENDAR_FALLBACK_GROUP = 'Events';

/**
 * Normalize untrusted block JSON config before the Vue block uses it.
 */
export function normalizeEventListCalendarConfig(config) {
  const eventGroups = Array.isArray(config.eventGroups)
    ? config.eventGroups.filter(groupId => EVENT_LIST_CALENDAR_GROUP_IDS.includes(groupId))
    : [];
  const timeSpan = TIME_SPANS.includes(config.timeSpan) ? config.timeSpan : 'this_month';
  const layout = LAYOUTS.includes(config.layout) ? config.layout : 'list';

  return {
    eventGroups: eventGroups.length ? eventGroups : EVENT_LIST_CALENDAR_GROUP_IDS,
    timeSpan,
    layout,
    hideExpiredEvents: config.hideExpiredEvents === true,
  };
}

/**
 * Build the default user filter state for the editor-selected event groups.
 */
export function eventListCalendarFilterDefaults(selectedGroupIds) {
  return EVENT_LIST_CALENDAR_GROUP_IDS.reduce((defaults, groupId) => {
    defaults[groupId] = selectedGroupIds.includes(groupId)
      ? { all: true, values: [] }
      : { all: false, values: [] };

    return defaults;
  }, {});
}

/**
 * Build the block's display and fetch date ranges for the selected time span.
 */
export function eventListCalendarRanges(timeSpan, activeView, displayTimezone, selectedMonthRange) {
  const todayKey = formatDateKey(new Date(), displayTimezone);

  if (timeSpan === 'today') {
    const range = {
      start_after: todayKey,
      start_before: addDays(todayKey, 1),
    };

    return {
      displayRange: range,
      fetchRange: range,
    };
  }

  if (timeSpan === 'this_month') {
    const displayRange = selectedMonthRange || monthRangeForDateKey(todayKey);

    return {
      displayRange,
      fetchRange:
        activeView === 'calendar' ? calendarGridRangeForRange(displayRange) : displayRange,
    };
  }

  const range = {
    start_after: todayKey,
    start_before: addDays(todayKey, 7 - dayOfWeek(todayKey)),
  };

  return {
    displayRange: range,
    fetchRange: range,
  };
}

/**
 * Build the current-month range used to seed the block's month selector.
 */
export function eventListCalendarCurrentMonthRange(displayTimezone) {
  return monthRangeForDateKey(formatDateKey(new Date(), displayTimezone));
}

function monthRangeForDateKey(dateKey) {
  const { year, month } = dateKeyParts(dateKey);

  return monthToRange(year, month);
}

function calendarGridRangeForRange(range) {
  const { year, month } = dateKeyParts(range.start_after);

  return monthToCalendarGridRange(year, month);
}

function dayOfWeek(dateKey) {
  return dateForDateKey(dateKey).getDay();
}

function addDays(dateKey, days) {
  const date = dateForDateKey(dateKey);
  date.setDate(date.getDate() + days);

  return dateToKey(date);
}

function dateForDateKey(dateKey) {
  const [year, month, day] = dateKey.split('-').map(Number);

  return new Date(year, month - 1, day, 12, 0, 0);
}

function dateKeyParts(dateKey) {
  const [year, month] = dateKey.split('-');

  return {
    year: Number(year),
    month,
  };
}

function dateToKey(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}
